import React from "react";
import { HexColorPicker } from "react-colorful";
import { ArrowContainer, Popover } from "react-tiny-popover";
import "./ColorPicker.scss";

type ColorPickerProps = {
  value: string | null | undefined;
  onChange: (value: string | null | undefined) => void;
  validate?: (value: string) => string;
};

const isValidHexColor = (hexCode: string) => {
  const hexPattern = /^#([a-fA-F0-9]{3}|[a-fA-F0-9]{6})$/;

  return hexPattern.test(hexCode);
};

const ColorPicker = ({ onChange, value, validate }: ColorPickerProps) => {
  const [open, setOpen] = React.useState(false);
  const [internalColor, setInternalColor] = React.useState(value);
  const toggleRef = React.useRef<HTMLButtonElement>(null);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (open) {
      setInternalColor(value);
      setError("");
    }
  }, [open, value]);

  return (
    <div className="color-picker">
      <Popover
        ref={toggleRef}
        isOpen={open}
        padding={-12}
        positions={["bottom"]}
        containerClassName="color-picker-popover"
        content={({ position, childRect, popoverRect }) => (
          <ArrowContainer
            position={position}
            childRect={childRect}
            popoverRect={popoverRect}
            arrowColor="var(--border-color)"
            arrowSize={20}
            arrowStyle={{}}
            arrowClassName="color-picker-popover-arrow"
          >
            <div className="color-picker-popover-inner">
              <HexColorPicker
                color={value ?? ""}
                onChange={(color) => {
                  setInternalColor(color);

                  const errors =
                    validate && internalColor ? validate(internalColor) : "";
                  setError(errors);
                }}
              />
              <div
                className="color-picker-preview"
                style={{
                  backgroundColor: internalColor ?? "white",
                }}
              ></div>
              <div className="color-picker-input-row">
                <input
                  value={internalColor ?? ""}
                  onChange={(event) => {
                    setInternalColor(event.target.value);
                    if (error) {
                      const errors =
                        validate && event.target.value
                          ? validate(event.target.value)
                          : "";
                      setError(errors);
                    }
                  }}
                />
                {!!error && (
                  <div className="color-picker-error-row">{error}</div>
                )}
              </div>
              <div className="color-picker-bottom-row">
                <button
                  onClick={() => {
                    setError("");
                    setOpen(false);
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (!internalColor || isValidHexColor(internalColor)) {
                      const errors =
                        validate && internalColor
                          ? validate(internalColor)
                          : "";
                      if (errors) {
                        setError(errors);
                      } else {
                        onChange(internalColor);
                        setOpen(false);
                        setError("");
                      }
                    } else {
                      setError("Invalid Hex Code");
                    }
                  }}
                >
                  Save
                </button>
              </div>
            </div>
          </ArrowContainer>
        )}
      >
        <button
          className="color-picker-swatch"
          onClick={() => setOpen((prev) => !prev)}
        >
          <div
            className="swatch-inner"
            style={{
              backgroundColor: value ?? "white",
            }}
          />
        </button>
      </Popover>
    </div>
  );
};

export default ColorPicker;
